import type { BlockStyles, ImageSettings, TextStyles } from "./types";

export { SelectionElement } from "./types";
export type { BlockStyles, ImageSettings, TextStyles } from "./types";

export const createDefaultImageSettings = (): ImageSettings => ({
  src: "",
  isBackground: false,
  alt: "",
  borderRadius: "",
  baseWidth: 0,
  baseHeight: 0,
  width: "",
  height: "",
  objectFit: "fill",
  paddingTop: "",
  paddingRight: "",
  paddingBottom: "",
  paddingLeft: "",
  marginTop: "",
  marginRight: "",
  marginBottom: "",
  marginLeft: "",
});

export const createDefaultBlockStyles = (): BlockStyles => ({
  width: "",
  height: "",
  paddingTop: "",
  paddingRight: "",
  paddingBottom: "",
  paddingLeft: "",
  marginTop: "",
  marginRight: "",
  marginBottom: "",
  marginLeft: "",
  backgroundColor: "",
});

export const createDefaultTextStyles = (): TextStyles => ({
  fontFamily: "",
  fontSize: "",
  fontWeight: "",
  fontStyle: "",
  textAlign: "",
  textDecoration: "",
  verticalAlign: "",
  color: "#000000",
});

const FONT_SIZE_PRESETS = [
  8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 80, 96,
  128,
];

/**
 * Preset font sizes plus the selection's current size when it isn't a preset
 * (e.g. a Figma import's 22px body copy). Without the injected option the
 * Select can't display the real value and falls back to its placeholder,
 * which reads as a wrong-but-plausible size to the user.
 */
export const buildFontSizeOptions = (
  currentSize: string,
): { label: string; value: string }[] => {
  const base = FONT_SIZE_PRESETS.map((size) => ({
    label: `${size}px`,
    value: String(size),
  }));
  const current = Number(currentSize);
  if (
    !currentSize ||
    !Number.isFinite(current) ||
    FONT_SIZE_PRESETS.includes(current)
  ) {
    return base;
  }
  const option = { label: `${currentSize}px`, value: currentSize };
  const insertAt = base.findIndex((o) => Number(o.value) > current);
  return insertAt === -1
    ? [...base, option]
    : [...base.slice(0, insertAt), option, ...base.slice(insertAt)];
};

/**
 * HTML tags treated as block-level containers in the editor (tables, divs, sections).
 */
export const BLOCK_TAGS = new Set([
  "table",
  "tr",
  "td",
  "th",
  "thead",
  "tbody",
  "tfoot",
  "div",
  "section",
]);

/**
 * Ensures a CSS measurement has a unit — bare numbers get "px" appended, values with units pass through.
 */
export const normalizeMeasurement = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return `${trimmed}px`;
  }
  return trimmed;
};

/**
 * Removes the trailing "px" suffix from a CSS value so it can be used as a raw number (e.g. "12px" → "12").
 */
export const stripPx = (value: string | null | undefined) => {
  if (!value) return "";
  return value.endsWith("px") ? value.slice(0, -2) : value;
};

// Unit suffixes that indicate a size depends on its container or the viewport
// rather than being fixed. Viewport units (vw/vh + their small/large/dynamic
// variants and vmin/vmax) are extremely rare in email HTML, but included for
// completeness so any future non-email surface using this helper is covered.
const RESPONSIVE_UNIT_SUFFIXES = [
  "%",
  "vw",
  "vh",
  "vmin",
  "vmax",
  "svw",
  "svh",
  "lvw",
  "lvh",
  "dvw",
  "dvh",
];

const isResponsiveSizeValue = (value: string | null | undefined): boolean => {
  if (!value) return false;
  if (value === "auto") return true;
  return RESPONSIVE_UNIT_SUFFIXES.some((suffix) => value.endsWith(suffix));
};

/**
 * Detects whether an <img> is sized responsively on each axis — i.e. its width
 * or height is expressed as `auto`, a percentage, or a viewport-relative unit
 * via either the HTML attribute or the inline style. Used to avoid pinning
 * responsive images to pixel dimensions on selection, which would destroy
 * their ability to scale with the viewport.
 *
 * Note: this does not unwrap `calc()` / `min()` / `max()` expressions —
 * `style="width: calc(100% - 20px)"` is treated as non-responsive because the
 * outer token is the closing paren, not a recognized unit. Email HTML doesn't
 * use these in practice.
 */
export const isImageResponsivelySized = (
  image: HTMLImageElement,
): { width: boolean; height: boolean } => {
  return {
    width:
      isResponsiveSizeValue(image.getAttribute("width")) ||
      isResponsiveSizeValue(image.style.width),
    height:
      isResponsiveSizeValue(image.getAttribute("height")) ||
      isResponsiveSizeValue(image.style.height),
  };
};

/**
 * Reads the border-radius from an element — prefers the inline style, falls back to computed style.
 */
export const extractBorderRadius = (image: HTMLElement): string => {
  const inlineValue = image.style.borderRadius;
  if (inlineValue && inlineValue.trim() !== "") {
    return stripPx(inlineValue.trim());
  }

  const computedValue =
    image.ownerDocument?.defaultView?.getComputedStyle(image).borderRadius;
  if (computedValue && computedValue.trim() !== "" && computedValue !== "0px") {
    return stripPx(computedValue.trim());
  }

  return "";
};

/**
 * Expands a 3-character shorthand hex color to its full 6-character form (e.g. "#abc" → "#aabbcc").
 */
const expandShorthandHex = (value: string) => {
  if (value.length === 4) {
    const [, r, g, b] = value;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return value;
};

/**
 * Converts any CSS color string (hex shorthand, rgb(), rgba()) to a lowercase 6-digit hex. Returns "#000000" for invalid input.
 */
export const toHexColor = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "#000000";
  }
  if (trimmed.startsWith("#")) {
    if (trimmed.length === 4 || trimmed.length === 7) {
      return expandShorthandHex(trimmed.toLowerCase());
    }
    return "#000000";
  }
  const match = trimmed.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (match) {
    const rgb = match.slice(1, 4).map((part) => {
      const parsed = Number(part);
      if (Number.isNaN(parsed)) return 0;
      return Math.max(0, Math.min(255, parsed));
    });
    const [r, g, b] = rgb;
    const hex = ((r || 0) << 16) | ((g || 0) << 8) | (b || 0);
    return `#${hex.toString(16).padStart(6, "0")}`;
  }
  return "#000000";
};

/**
 * Returns true when an anchor is styled as a button — has a `bgcolor`
 * attribute, an inline background-color, or a computed non-transparent
 * background (from a class like `.dmbuttonPrimary`). Email templates
 * frequently use <a> as buttons because many clients (notably older
 * Outlook) ignore <button> styling.
 */
export const isAnchorButton = (el: Element): el is HTMLAnchorElement => {
  if (el.tagName.toLowerCase() !== "a") return false;
  const a = el as HTMLAnchorElement;
  if (a.hasAttribute("bgcolor")) return true;
  if (a.style.backgroundColor) return true;
  const bg = a.ownerDocument?.defaultView?.getComputedStyle(a).backgroundColor;
  return !!bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)";
};

// `[^()]*` is deliberate: it cannot match the closing paren, so the regex
// never backtracks (an ambiguous pattern like `\s*(['"]?)[^'"()]+\1\s*` is
// super-quadratic on `url(` followed by lots of whitespace). Quote stripping
// happens in unquoteCssUrl instead.
const CSS_URL_PATTERN = /url\(([^()]*)\)/i;

const unquoteCssUrl = (raw: string): string => {
  const trimmed = raw.trim();
  const quoted = /^(['"])([\s\S]*)\1$/.exec(trimmed);
  if (quoted?.[2] !== undefined) {
    return quoted[2];
  }
  return trimmed;
};

/**
 * Reads the image URL from an element's inline `background-image` (the CSSOM
 * also surfaces the `background` shorthand here). Multi-layer values (e.g. a
 * gradient overlay + url) yield the first url() layer. Returns null for
 * gradient-only or missing inline background images. Email templates commonly
 * paint hero images as div/td backgrounds so CSS can crop or swap them per
 * layout; the editor treats those elements as image targets.
 */
export const getInlineBackgroundImageUrl = (el: Element): string | null => {
  const style = (el as HTMLElement).style;
  if (!style) return null;
  const match = style.backgroundImage?.match(CSS_URL_PATTERN);
  if (!match) return null;
  return unquoteCssUrl(match[1] ?? "") || null;
};

/**
 * Builds a `background-image` value that swaps the first url() layer of
 * `existingValue` for `url`, preserving any other layers (hero sections often
 * stack a dimming gradient over the image: `linear-gradient(...), url(...)`).
 * When `existingValue` has non-url layers but no url() (e.g. after clearing
 * the Source URL), appends the url so those layers survive a clear-then-paste.
 * When `existingValue` is empty/`none`, returns just the new url. The URL is
 * double-quoted with quotes/backslashes escaped so URLs containing apostrophes
 * can't truncate the declaration or smuggle in extra layers.
 */
export const replaceCssBackgroundImageUrl = (
  existingValue: string,
  url: string,
): string => {
  const escaped = url.replace(/[\\"]/g, (char) => `\\${char}`);
  const cssUrl = `url("${escaped}")`;
  if (CSS_URL_PATTERN.test(existingValue)) {
    // Function replacement so `$&`-style sequences in the URL stay literal.
    return existingValue.replace(CSS_URL_PATTERN, () => cssUrl);
  }
  const trimmed = existingValue.trim();
  if (trimmed && trimmed !== "none") {
    return `${trimmed}, ${cssUrl}`;
  }
  return cssUrl;
};

/**
 * Removes the first url() layer from a `background-image` value, preserving
 * any other layers (e.g. dimming gradients). Returns "" when nothing remains.
 */
export const removeCssBackgroundImageUrl = (existingValue: string): string => {
  if (!existingValue) return "";
  const stripped = existingValue.replace(CSS_URL_PATTERN, "");
  if (stripped === existingValue) return existingValue;
  return stripped
    .replace(/\s*,\s*,\s*/g, ", ")
    .replace(/^\s*,\s*|\s*,\s*$/g, "")
    .trim();
};

/**
 * Reads the image URL the element is *currently rendering* as its background.
 * Computed style reflects active media queries, so in mobile preview this
 * returns the mobile-swap image from a `<style>` block rule rather than the
 * inline (desktop) value. Returns null when nothing is rendered or the
 * element has no view (detached documents).
 */
export const getRenderedBackgroundImageUrl = (el: Element): string | null => {
  const view = el.ownerDocument?.defaultView;
  if (!view) return null;
  const match = view
    .getComputedStyle(el)
    .backgroundImage?.match(CSS_URL_PATTERN);
  if (!match) return null;
  return unquoteCssUrl(match[1] ?? "") || null;
};

const CSS_URL_PATTERN_GLOBAL = /url\(([^()]*)\)/gi;

/**
 * True when two CSS image URLs refer to the same resource. Computed style
 * typically absolutizes urls, while authored stylesheet text often keeps
 * relative forms — exact string equality alone misses those.
 */
export const cssBackgroundImageUrlsEqual = (
  a: string,
  b: string,
  baseHref: string,
): boolean => {
  if (a === b) return true;
  try {
    if (new URL(a, baseHref).href === new URL(b, baseHref).href) {
      return true;
    }
  } catch {
    // Ignore unresolvable URLs and fall through to path-suffix matching.
  }
  // Fallback when the document base differs from the one the browser used
  // to absolutize (common with srcdoc iframes): treat a relative path as
  // equal to an absolute URL that ends at a path segment boundary.
  const [longer, shorter] = a.length >= b.length ? [a, b] : [b, a];
  if (!shorter) return false;
  return (
    longer.endsWith(shorter) &&
    (longer.length === shorter.length ||
      longer.charAt(longer.length - shorter.length - 1) === "/")
  );
};

/**
 * Rewrites every `url(<oldUrl>)` reference inside the document's `<style>`
 * elements to point at `newUrl`. Used to edit an image that is visible via a
 * stylesheet rule (e.g. a mobile media-query swap) rather than the element's
 * inline style. New references are double-quoted with quotes/backslashes
 * escaped, matching replaceCssBackgroundImageUrl. Pass an empty `newUrl` to
 * clear matching references to bare `none`. Returns true when at least one
 * reference was rewritten.
 */
export const replaceUrlInStyleElements = (
  doc: Document,
  oldUrl: string,
  newUrl: string,
): boolean => {
  // Empty newUrl clears the image: emit bare `none` so media-query overrides
  // stop painting without inventing url(""). Non-empty paths stay quoted.
  const replacement = newUrl
    ? `url("${newUrl.replace(/[\\"]/g, (char) => `\\${char}`)}")`
    : "none";
  const baseHref = doc.baseURI || doc.URL || "";
  let replaced = false;
  for (const styleEl of Array.from(doc.querySelectorAll("style"))) {
    const css = styleEl.textContent;
    if (!css) continue;
    const newCss = css.replace(CSS_URL_PATTERN_GLOBAL, (fullMatch, raw) => {
      if (
        !cssBackgroundImageUrlsEqual(
          unquoteCssUrl(String(raw)),
          oldUrl,
          baseHref,
        )
      ) {
        return fullMatch;
      }
      replaced = true;
      return replacement;
    });
    if (newCss !== css) {
      styleEl.textContent = newCss;
    }
  }
  return replaced;
};

/**
 * True when the element should classify as an image selection because it
 * paints an inline background image and has no direct text of its own.
 * Elements with direct text keep text selection so the copy stays editable.
 */
export const isBackgroundImageTarget = (el: Element): boolean =>
  getInlineBackgroundImageUrl(el) !== null && !hasDirectNonWhitespaceText(el);

/**
 * Reads background-color from whichever source the email actually uses —
 * inline style, then the legacy `bgcolor` attribute, then computed style
 * (catches class-based styling). Returns "" when no background is set.
 */
export const readBackgroundColor = (el: HTMLElement): string => {
  if (el.style.backgroundColor) {
    return toHexColor(el.style.backgroundColor);
  }
  const bgAttr = el.getAttribute("bgcolor");
  if (bgAttr) {
    return toHexColor(bgAttr);
  }
  const bg =
    el.ownerDocument?.defaultView?.getComputedStyle(el).backgroundColor;
  if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)") {
    return toHexColor(bg);
  }
  return "";
};

/**
 * Rendered border-box size of an element on one axis, as a unitless px
 * string. Email HTML has no `box-sizing` reset, so `getComputedStyle().width`
 * reports the content box — a 600px-wide table cell with horizontal padding
 * reads as 520/560, which users misread as a broken layout. `offsetWidth`/
 * `offsetHeight` are the border-box the element visually occupies regardless
 * of box-sizing (matching how design tools report size). Falls back to the
 * inline/computed value when the element has no layout (e.g. display:none).
 */
const readRenderedDimension = (
  el: HTMLElement,
  computed: CSSStyleDeclaration | undefined,
  axis: "width" | "height",
): string => {
  const offset = axis === "width" ? el.offsetWidth : el.offsetHeight;
  if (offset > 0) {
    return String(offset);
  }
  return stripPx(el.style[axis] || computed?.[axis] || "");
};

/**
 * Converts a user-entered border-box size into the value to write to CSS
 * `width`/`height` for this element. Under the email-HTML default
 * `box-sizing: content-box`, CSS width excludes padding and border, so the
 * entered visual size must have those edges subtracted or the element grows
 * by its padding on every edit (asymmetric with `readRenderedDimension`,
 * which reports border-box). Non-px values (%, auto, …) pass through
 * unchanged, as does an element with no rendered layout on the axis —
 * there `readRenderedDimension` displayed the raw CSS value, so the typed
 * value is written back in the same terms.
 *
 * Returns `null` when the entered size is smaller than the element's
 * padding+border on the axis: no CSS size can render a border-box that
 * small, and such values are usually a larger number mid-keystroke
 * ("6", "60" on the way to "600") — writing a clamped 0 would visibly
 * collapse the element while the user types. Callers skip the write.
 */
export const borderBoxSizeToCssValue = (
  el: HTMLElement,
  axis: "width" | "height",
  measurement: string,
): string | null => {
  const match = /^(\d+(?:\.\d+)?)px$/.exec(measurement);
  if (!match || !match[1]) {
    return measurement;
  }
  const offset = axis === "width" ? el.offsetWidth : el.offsetHeight;
  if (offset <= 0) {
    return measurement;
  }
  const computed = el.ownerDocument?.defaultView?.getComputedStyle(el);
  if (!computed || computed.boxSizing === "border-box") {
    return measurement;
  }
  const edges =
    axis === "width"
      ? [
          computed.paddingLeft,
          computed.paddingRight,
          computed.borderLeftWidth,
          computed.borderRightWidth,
        ]
      : [
          computed.paddingTop,
          computed.paddingBottom,
          computed.borderTopWidth,
          computed.borderBottomWidth,
        ];
  const inset = edges.reduce((sum, edge) => sum + (parseFloat(edge) || 0), 0);
  const contentSize = parseFloat(match[1]) - inset;
  if (contentSize < 0) {
    return null;
  }
  return `${contentSize}px`;
};

/**
 * Reads sizing/spacing/background into `BlockStyles`, preferring inline
 * styles and falling back to computed style. Width/height report the
 * rendered border-box size (see `readRenderedDimension`).
 */
export const computeBlockStyles = (el: HTMLElement): BlockStyles => {
  const computed = el.ownerDocument?.defaultView?.getComputedStyle(el);
  return {
    width: readRenderedDimension(el, computed, "width"),
    height: readRenderedDimension(el, computed, "height"),
    paddingTop: stripPx(el.style.paddingTop || computed?.paddingTop || ""),
    paddingRight: stripPx(
      el.style.paddingRight || computed?.paddingRight || "",
    ),
    paddingBottom: stripPx(
      el.style.paddingBottom || computed?.paddingBottom || "",
    ),
    paddingLeft: stripPx(el.style.paddingLeft || computed?.paddingLeft || ""),
    marginTop: stripPx(el.style.marginTop || computed?.marginTop || ""),
    marginRight: stripPx(el.style.marginRight || computed?.marginRight || ""),
    marginBottom: stripPx(
      el.style.marginBottom || computed?.marginBottom || "",
    ),
    marginLeft: stripPx(el.style.marginLeft || computed?.marginLeft || ""),
    backgroundColor: readBackgroundColor(el),
  };
};

/**
 * Returns the path of child indices from `root` down to `target`
 * (exclusive of `root`). Returns null if `target` is not a descendant
 * of `root`. Used to preserve element selection across an iframe
 * document swap: record the path before swap and walk it on the new
 * document. Works for style-only edits; fails gracefully after
 * structural changes (e.g. link wrap/unwrap) since the path will
 * resolve to a different element or to null.
 */
export const getNodePath = (root: Node, target: Node): number[] | null => {
  const path: number[] = [];
  let current: Node | null = target;
  while (current && current !== root) {
    const parent: Node | null = current.parentNode;
    if (!parent) return null;
    const index = Array.prototype.indexOf.call(parent.childNodes, current);
    if (index < 0) return null;
    path.unshift(index);
    current = parent;
  }
  return current === root ? path : null;
};

/**
 * Resolves a path produced by `getNodePath` against a (possibly new)
 * `root`. Returns null if any step in the path is missing.
 */
export const getNodeAtPath = (root: Node, path: number[]): Node | null => {
  let current: Node = root;
  for (const idx of path) {
    const child = current.childNodes[idx];
    if (!child) return null;
    current = child;
  }
  return current;
};

export const isBold = (textStyles: TextStyles) =>
  Number(textStyles.fontWeight) >= 700;

export const isItalic = (textStyles: TextStyles) =>
  textStyles.fontStyle === "italic";

export const isUnderline = (textStyles: TextStyles) =>
  textStyles.textDecoration.includes("underline");

export const isStrikethrough = (textStyles: TextStyles) =>
  textStyles.textDecoration.includes("line-through");

/**
 * The `text-decoration` values the toolbar and sidebar toggle. Both live in the
 * one `textDecoration` field and compose, so toggling either has to preserve
 * the other — unlike the mutually exclusive baseline shifts below.
 */
export type TextDecorationMark = "underline" | "line-through";

/** The two baseline shifts the superscript/subscript toggles produce. */
export type BaselineShift = "super" | "sub";

/**
 * Whether a `vertical-align` value is one of the two shifts the toggles own.
 * Note these are the CSS values, not the tag names — superscript is
 * `vertical-align: super` on a `<sup>`.
 */
export const isBaselineShift = (value: string): value is BaselineShift =>
  value === "super" || value === "sub";

/** The semantic tag each baseline shift is emitted as. */
export const BASELINE_SHIFT_TAG: Record<BaselineShift, "sup" | "sub"> = {
  super: "sup",
  sub: "sub",
};

/**
 * Baseline shift implied by a semantic tag. Lets an imported `<sup>` footnote
 * marker (which carries no inline `vertical-align` of its own) still read as
 * superscript, so the toggle shows active when the cursor lands inside it.
 */
export const baselineShiftForTag = (tag: string): BaselineShift | null => {
  if (tag === "sup") return "super";
  if (tag === "sub") return "sub";
  return null;
};

/**
 * Size a superscript/subscript glyph renders at, relative to its context.
 * A percentage keeps it proportional when the surrounding font size changes
 * later, and is honored by every major client (unlike `smaller`).
 */
const BASELINE_SHIFT_FONT_SIZE = "75%";

/** CSS properties the baseline-shift declarations own, so clearing the
 * toggle removes exactly what it added. Kept in lockstep with
 * `baselineShiftDeclarations` — a property listed here but never emitted would
 * strip an author's own declaration on clear. */
export const BASELINE_SHIFT_PROPERTIES = [
  "vertical-align",
  "font-size",
] as const;

/**
 * Inline CSS emitted alongside a `<sup>`/`<sub>` tag.
 *
 * The tag alone isn't enough: Outlook and Gmail are inconsistent about
 * applying UA styles to a bare `<sup>`/`<sub>`, so the shift and the size
 * reduction are spelled out. This is the same pair the rest of the codebase
 * inlines for these tags — see `UA_TAG_TEXT_PROPS` in dom-to-html and
 * `TAG_IMPLIED_PROPS` in the ad-studio inline-format module.
 *
 * Deliberately no `line-height: 0`. It is the usual trick for stopping a raised
 * glyph from opening up the paragraph's leading, but it collapses *every* line
 * box inside the element it lands on, and neither surface can promise that
 * element holds a single short glyph: the element-level path applies these
 * declarations to whatever the user selected (routinely a block `<p>`), and the
 * range path wraps whatever was selected (which may span a line break). Both
 * then render every line of that text on top of the one above it. Slightly
 * looser leading around a footnote marker is the cheaper defect.
 *
 * Keys are kebab-case CSS property names, so they pair with
 * `BASELINE_SHIFT_PROPERTIES` and go straight into `style.setProperty`.
 */
export const baselineShiftDeclarations = (
  align: BaselineShift,
): Record<string, string> => ({
  "vertical-align": align,
  "font-size": BASELINE_SHIFT_FONT_SIZE,
});

export const isSuperscript = (textStyles: TextStyles) =>
  textStyles.verticalAlign === "super";

export const isSubscript = (textStyles: TextStyles) =>
  textStyles.verticalAlign === "sub";

/**
 * Normalizes CSS font-weight keywords to numeric equivalents ("normal" → "400", "bold" → "700") for consistent state storage.
 */
export const normalizeFontWeightForState = (value: string) => {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return "";
  if (trimmed === "normal") return "400";
  if (trimmed === "bold") return "700";
  if (/^\d+$/.test(trimmed)) {
    return trimmed;
  }
  return "";
};

/**
 * Walks the full subtree of an element and returns true if any text node contains non-whitespace characters.
 */
export const hasNonWhitespaceText = (el: Element) => {
  const ownerDoc = el.ownerDocument ?? document;
  const walker = ownerDoc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  while (node) {
    const text = (node as Text).nodeValue || "";
    if (text.trim().length > 0) return true;
    node = walker.nextNode();
  }
  return false;
};

/**
 * Returns true if the element has a direct (non-nested) child text node with non-whitespace content.
 */
export const hasDirectNonWhitespaceText = (el: Element) => {
  return Array.from(el.childNodes).some((node) => {
    if (node.nodeType !== Node.TEXT_NODE) {
      return false;
    }
    const value = (node as Text).nodeValue ?? "";
    return value.trim().length > 0;
  });
};

/**
 * Returns true if `node` has a following sibling that renders visible content.
 *
 * Used to decide whether a line-break <br> inserted at the caret is effectively
 * trailing — a lone trailing <br> at a block's end does not render a new line,
 * so it needs a filler <br>. Empty/whitespace-only text nodes and comments are
 * skipped because they collapse to nothing: pretty-printed templates leave a
 * whitespace text node (e.g. "\n  ") right after the text, which a bare
 * `nextSibling` check would wrongly treat as content.
 */
export const hasRenderedContentAfter = (node: Node): boolean => {
  for (let sibling = node.nextSibling; sibling; sibling = sibling.nextSibling) {
    if (sibling.nodeType === Node.ELEMENT_NODE) return true;
    if (
      sibling.nodeType === Node.TEXT_NODE &&
      ((sibling as Text).nodeValue ?? "").trim().length > 0
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Places the text caret at the given (x, y) coordinates inside a document.
 * Uses caretPositionFromPoint (Firefox) with a WebKit/Blink fallback.
 */
export const placeCaretAtPoint = (doc: Document, x: number, y: number) => {
  const caretPos = doc.caretPositionFromPoint?.(x, y);
  if (caretPos) {
    const sel = doc.getSelection();
    if (sel) {
      sel.collapse(caretPos.offsetNode, caretPos.offset);
    }
    return;
  }
  // Fallback for WebKit/Blink
  if ((doc as any).caretRangeFromPoint) {
    const range = (doc as any).caretRangeFromPoint(x, y) as Range | null;
    if (range) {
      const sel = doc.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }
};

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target) return false;
  const el = target as HTMLElement;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
}

// Input types that accept typed text and therefore own their own browser-
// native text-editing undo stack. Non-text inputs (radio, checkbox, button,
// etc.) are excluded — they have no undo stack we'd be trampling.
const TEXT_INPUT_TYPES = new Set([
  "text",
  "password",
  "email",
  "url",
  "search",
  "tel",
  "number",
  "date",
  "datetime-local",
  "month",
  "week",
  "time",
]);

/**
 * Tighter variant of `isEditableTarget` used to gate Cmd+Z hijacking.
 * Only returns true for targets that own a native text-editing undo stack
 * we should not interfere with. Radio/checkbox/button inputs (e.g. the
 * alignment SegmentedControl in the sidebar, which renders as radios)
 * return false so Cmd+Z still routes to our custom undo stack.
 */
export function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!target) return false;
  const el = target as HTMLElement;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA") return true;
  if (el.tagName === "INPUT") {
    // Default <input> type is "text" when unspecified.
    const type = (el as HTMLInputElement).type?.toLowerCase() || "text";
    return TEXT_INPUT_TYPES.has(type);
  }
  return false;
}

/**
 * Replaces the iframe document's root element in-place with the HTML snapshot.
 * Preserves the contentWindow (and therefore scrollY) — unlike changing srcDoc
 * or remounting the iframe, which reloads the window.
 *
 * Scripts in the snapshot will NOT execute. Email templates don't contain
 * scripts, so this is intentional; if one sneaks in, we want it inert.
 */
export function replaceIframeDocument(doc: Document, html: string): void {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const newRoot = doc.importNode(parsed.documentElement, true);
  doc.replaceChild(newRoot, doc.documentElement);
}

const EDITOR_CLASSES = new Set(["ht-selected-element", "ht-hover-element"]);

/**
 * Returns true if the element has attributes that likely came from the
 * original email source (class, id, data-*, etc.) rather than from the
 * editor. Editor-managed attributes (style, contenteditable, editor
 * highlight classes) are ignored.
 */
const hasSourceAttributes = (element: HTMLElement): boolean => {
  for (const attr of Array.from(element.attributes)) {
    if (attr.name === "style" || attr.name === "contenteditable") continue;
    if (attr.name === "class") {
      const hasNonEditorClass = attr.value
        .split(/\s+/)
        .some((c) => c && !EDITOR_CLASSES.has(c));
      if (!hasNonEditorClass) continue;
    }
    return true;
  }
  return false;
};

/**
 * The editor's own generic formatting wrapper. It carries no meaning beyond the
 * inline styles on it, so an empty one is always safe to remove — whatever
 * cleared the last style, and whether or not the editor created this particular
 * wrapper.
 */
const GENERIC_WRAPPER_TAG = "span";

/**
 * Tags where the tag *itself* is the formatting, so clearing that formatting has
 * to take the tag with it — a stripped `<sup>` would keep both its UA baseline
 * shift and its screen-reader announcement, unlike a `<b style="font-weight:
 * 400">`, which renders exactly like its surroundings and is therefore
 * neutralized in place.
 *
 * Emptiness alone does not qualify one of these for removal: an imported
 * `<sup>1</sup>` footnote marker carries no inline style to begin with, so it is
 * *permanently* "empty" and would be destroyed by any unrelated clear. They are
 * only unwrapped when the baseline shift is the thing being cleared — see
 * `shouldUnwrapElement`.
 */
const BASELINE_SHIFT_WRAPPER_TAGS = new Set(["sup", "sub"]);

type ShouldUnwrapOptions = {
  /**
   * Set only when the change that emptied this wrapper was the baseline shift
   * itself (`applyTextStyle("verticalAlign", …)`). Gates removal of a
   * `<sup>`/`<sub>` tag, which must survive every *other* field's clear.
   *
   * Defaults to false so a caller that forgets it leaves the tag in place — a
   * cosmetic wart if superscript was genuinely being turned off, rather than
   * silently dropping a footnote marker's shift.
   */
  isClearingBaselineShift?: boolean;
};

/**
 * Returns true if a formatting wrapper should be unwrapped because it no
 * longer carries any inline styles and has no source attributes.
 *
 * The editor creates these wrappers for inline formatting (bold, italic,
 * underline, superscript, etc.). When all styles are removed, the wrapper is
 * meaningless and should be cleaned up.
 *
 * A `<sup>`/`<sub>` additionally requires `isClearingBaselineShift`, because
 * emptiness doesn't mean the same thing for a tag that *is* the formatting.
 */
export const shouldUnwrapElement = (
  element: HTMLElement,
  { isClearingBaselineShift = false }: ShouldUnwrapOptions = {},
): boolean => {
  const tag = element.tagName.toLowerCase();
  const isUnwrappableWrapper =
    tag === GENERIC_WRAPPER_TAG ||
    (isClearingBaselineShift && BASELINE_SHIFT_WRAPPER_TAGS.has(tag));
  if (!isUnwrappableWrapper) return false;
  if (!element.parentElement) return false;
  if (hasSourceAttributes(element)) return false;
  return element.style.length === 0;
};

// Elements whose children may only be table structure. The HTML parser
// foster-parents any <a> found directly inside one out of the table, so an
// anchor placed here survives in the live DOM but vanishes the next time the
// document is parsed — i.e. the link is lost on save/reload.
const TABLE_CONTAINER_TAGS = new Set([
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
]);

const isTableContainer = (node: Node | null | undefined): boolean =>
  !!node &&
  node.nodeType === Node.ELEMENT_NODE &&
  TABLE_CONTAINER_TAGS.has((node as Element).tagName.toLowerCase());

/**
 * Insert a new empty-href <a> around `element`, falling back to wrapping the
 * element's contents when an anchor around it would be foster-parented out of
 * a table (e.g. a `<td>` whose text has no inline wrapper — an anchor between
 * `<tr>` and `<td>` is dropped on the next parse).
 *
 * Returns null when no placement survives a parse, so callers can tell the user
 * rather than reporting a link they didn't get.
 *
 * `wrapContents` forces the contents form for callers with their own reason to
 * keep `element` outermost.
 */
export const insertAnchorAround = (
  doc: Document,
  element: HTMLElement,
  wrapContents = false,
): HTMLAnchorElement | null => {
  // The contents form puts the anchor inside table structure; the outside form
  // puts it between the element and its parent. Only when the element is a
  // table container AND the outside form is unavailable does no placement
  // survive — e.g. a <tbody>, where both forms land in table-structure
  // position. A <table> in a <td> is fine: an anchor around it re-parses.
  const mustWrapContents = wrapContents || isTableContainer(element.parentNode);
  if (mustWrapContents && isTableContainer(element)) return null;

  const anchor = doc.createElement("a");
  anchor.setAttribute("href", "");

  if (mustWrapContents) {
    while (element.firstChild) {
      anchor.appendChild(element.firstChild);
    }
    element.appendChild(anchor);
  } else {
    element.parentNode?.replaceChild(anchor, element);
    anchor.appendChild(element);
  }

  return anchor;
};

// Liquid / personalization tokens: `{{ ... }}` (output) and `{% ... %}` (tags).
// Non-greedy so adjacent tokens don't merge; `s` flag so a token spanning a
// newline still matches.
const LIQUID_TOKEN_REGEX = /\{\{.*?\}\}|\{%.*?%\}/gs;

const offsetSplitsLiquidToken = (container: Node, offset: number): boolean => {
  // Selection offsets are character positions only inside text nodes. For
  // element containers the offset is a child index, and a token lives within a
  // single text node, so an element boundary can never bisect one.
  if (container.nodeType !== Node.TEXT_NODE) return false;
  const text = container.textContent ?? "";
  for (const match of text.matchAll(LIQUID_TOKEN_REGEX)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (offset > start && offset < end) return true;
  }
  return false;
};

/**
 * True when either boundary of the selection falls *inside* a Liquid token.
 * Wrapping such a selection in a `<span>`/`<a>` would inject HTML into the
 * token (e.g. `{{ promo_code | default: "<span ...>CODE</span>" }}`), whose
 * nested quotes break the personalization at render time. Wrapping a token in
 * its entirety is harmless, so we only flag boundaries that cut through one.
 *
 * Limitation: if a token is already split across multiple text nodes (e.g. from
 * a prior partial edit) the boundary check can't see the whole token. The
 * common case — a clean token in a single text node — is covered.
 */
export const rangeSplitsLiquidToken = (range: Range): boolean =>
  offsetSplitsLiquidToken(range.startContainer, range.startOffset) ||
  offsetSplitsLiquidToken(range.endContainer, range.endOffset);

/**
 * Locked descendants of `root` (see `isElementLocked`). An unlocked element can
 * still CONTAIN locked content — e.g. a `<td>` holding static text next to an
 * inline dynamic-content slot — and stays inline-editable.
 */
export const lockedDescendants = (
  root: Element,
  isLocked: (el: Element) => boolean,
): Element[] => [...root.querySelectorAll("*")].filter(isLocked);

/**
 * True when the range reaches into any of `elements`. A caret immediately
 * before or after one doesn't count (a boundary point isn't an overlap), so
 * typing beside a slot stays allowed; a delete range enclosing it does.
 */
export const rangeTouchesElements = (
  range: Range,
  elements: Element[],
): boolean => elements.some((element) => range.intersectsNode(element));

/**
 * An ordered selection set. The first element is the "primary" — the source of
 * truth for all single-element editing (sidebar, inline text edit, link,
 * comments). `extras` are additional elements selected via Cmd/Ctrl-click and
 * are inspect/bulk-delete only. The set never contains an element that is an
 * ancestor or descendant of another member (see `addToSelection`).
 */
export type Selection = {
  primary: Element | null;
  extras: Element[];
};

const orderedSelection = (all: Element[]): Selection => ({
  primary: all[0] ?? null,
  extras: all.slice(1),
});

/**
 * Add `el` to the selection, enforcing the no-nesting invariant:
 *   - If `el` is contained by an existing member, it's a no-op — you can't
 *     select a descendant of something already selected.
 *   - Otherwise `el` absorbs (replaces) every existing member it contains, then
 *     joins the set. Clicking an ancestor of one or more selected elements thus
 *     swaps them out for the ancestor.
 *   - Selecting an element already in the set is a no-op (no duplicate).
 * The caller is responsible for DOM-side cleanup (removing highlight /
 * contenteditable) of the elements dropped from the returned set.
 */
export const addToSelection = (
  selection: Selection,
  el: Element,
): Selection => {
  const all = [selection.primary, ...selection.extras].filter(
    (e): e is Element => e !== null,
  );

  if (all.some((member) => member !== el && member.contains(el))) {
    return selection;
  }

  const survivors = all.filter(
    (member) => member !== el && !el.contains(member),
  );
  return orderedSelection([...survivors, el]);
};

/**
 * Toggle `el` out of the selection if present, otherwise add it (delegating to
 * `addToSelection` so the no-nesting rules apply). Used by Cmd/Ctrl-click.
 * Removing the primary promotes the next member; the caller re-classifies the
 * new primary when the set collapses back to one element.
 */
export const toggleInSelection = (
  selection: Selection,
  el: Element,
): Selection => {
  const all = [selection.primary, ...selection.extras].filter(
    (e): e is Element => e !== null,
  );
  if (all.includes(el)) {
    return orderedSelection(all.filter((member) => member !== el));
  }
  return addToSelection(selection, el);
};
